import React from 'react';
import { Users, Zap, ArrowRight, ShieldCheck, DollarSign, BarChart3, CheckCircle2 } from 'lucide-react';

export const ForProviders = ({ onStartSelling }) => {
  const scrollToFeatures = () => {
    const element = document.getElementById('provider-features');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="flex-1 w-full bg-white animate-fade-in">
      {/* Hero Section */}
      <div className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#190192]/40 via-purple-100 to-rose-100 opacity-70" />
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-x-12 lg:items-center">
            
            {/* Left Content */}
            <div className="pl-16 max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
              <div className="inline-flex items-center space-x-2 rounded-full bg-white/60 backdrop-blur-sm border border-indigo-100 px-3 py-1 mb-8 shadow-sm">
                <span className="flex h-2 w-2 rounded-full bg-indigo-500"></span>
                <span className="text-sm font-medium text-indigo-700">Now recruiting top talent</span>
              </div>
              
              <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-6xl mb-6">
                Supercharge your <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  digital service business
                </span>
              </h1>
              
              <p className="text-lg leading-8 text-slate-600 mb-8">
                Join PARROTOFFICE to access high-quality clients, automated booking tools, and AI-powered business support. Focus on your craft, we handle the rest.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <button 
                  onClick={onStartSelling}
                  className="w-full sm:w-auto rounded-full bg-indigo-600 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-700 hover:shadow-xl transition-all hover:-translate-y-0.5"
                >
                  Start Selling
                </button>
                <button 
                  onClick={scrollToFeatures}
                  className="w-full sm:w-auto rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-slate-700 shadow-md border border-slate-100 hover:bg-slate-50 transition-all group"
                >
                  Learn more <ArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              <div className="mt-10 flex items-center justify-center lg:justify-start gap-6 text-sm font-medium text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-indigo-500" />
                  <span>Verified Clients</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-indigo-500" />
                  <span>Secure Payments</span>
                </div>
              </div>
            </div>

            {/* Right Visual */}
            <div className="mt-16 lg:mt-0 relative max-w-md mx-auto lg:max-w-none perspective-1000">
              {/* Background Blob */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-indigo-200 to-purple-200 rounded-full blur-3xl opacity-40 -z-10 animate-pulse-slow" />

              {/* Card Interface */}
              <div className="relative bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50 p-6 transform rotate-y-6 rotate-x-6 hover:rotate-0 transition-transform duration-500">
                 {/* Header Mockup */}
                 <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-slate-900">New Lead</div>
                        <div className="text-xs text-slate-500">Just now</div>
                      </div>
                    </div>
                    <div className="px-2 py-1 bg-green-50 text-green-600 text-xs font-bold rounded">
                      High Match
                    </div>
                 </div>

                 {/* Body Mockup */}
                 <div className="space-y-4 mb-8">
                   <div className="flex justify-between items-center">
                     <span className="text-sm text-slate-500">Project Type</span>
                     <span className="text-sm font-medium text-slate-900">Brand Identity Redesign</span>
                   </div>
                   <div className="flex justify-between items-center">
                     <span className="text-sm text-slate-500">Budget</span>
                     <span className="text-sm font-bold text-slate-900">$2,500 - $4,000</span>
                   </div>
                   <div className="flex justify-between items-center">
                     <span className="text-sm text-slate-500">Client</span>
                     <span className="text-sm font-medium text-slate-900">TechCorp Inc.</span>
                   </div>
                 </div>

                 {/* Actions Mockup */}
                 <div className="grid grid-cols-2 gap-3">
                   <button className="py-2.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                     Decline
                   </button>
                   <button className="py-2.5 rounded-lg bg-indigo-600 text-sm font-medium text-white shadow-md shadow-indigo-200 hover:bg-indigo-700">
                     Accept Request
                   </button>
                 </div>
                 
                 {/* Floating Badge */}
                 <div className="absolute -right-6 -bottom-6 bg-white p-4 rounded-xl shadow-xl border border-slate-50 flex items-center gap-3 animate-bounce-slow">
                   <div className="p-2 bg-green-100 rounded-lg text-green-600">
                     <DollarSign className="w-6 h-6" />
                   </div>
                   <div>
                     <div className="text-xs text-slate-500 font-medium">Earnings this week</div>
                     <div className="text-lg font-bold text-slate-900">$1,240.00</div>
                   </div>
                 </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div id="provider-features" className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600">Grow Faster</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Everything you need to scale your agency
            </p>
            <p className="mt-6 text-lg leading-8 text-slate-600">
              We provide the infrastructure so you can provide the expertise. From guaranteed payments to project management tools, we've got you covered.
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {[
                {
                  name: 'Guaranteed Payments',
                  description: 'Get paid immediately upon project completion. Our escrow system ensures you never have to chase invoices again.',
                  icon: DollarSign,
                },
                {
                  name: 'Smart Matching',
                  description: 'Stop bidding on low-quality leads. Our AI matches you with verified businesses looking for your specific expertise.',
                  icon: ShieldCheck,
                },
                {
                  name: 'Business Analytics',
                  description: 'Track your earnings, profile performance, and customer satisfaction with our built-in provider dashboard.',
                  icon: BarChart3,
                },
              ].map((feature) => (
                <div key={feature.name} className="flex flex-col relative pl-16 group">
                  <dt className="text-base font-bold leading-7 text-slate-900">
                    <div className="absolute left-0 top-0 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 group-hover:bg-indigo-600 transition-colors duration-300">
                      <feature.icon className="h-6 w-6 text-indigo-600 group-hover:text-white transition-colors duration-300" aria-hidden="true" />
                    </div>
                    {feature.name}
                  </dt>
                  <dd className="mt-2 text-base leading-7 text-slate-600">
                    {feature.description}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};